import { Button, notification } from 'antd';
import cryptoJs from 'crypto-js';
import React from 'react';

import GiftCodeTable from '@/GiftCode/components/GiftCodeTable';
import { writeGiftCodeDataToSpreadsheet } from '@/GiftCode/service/spreadsheetAccessor';
import { CreateGiftCodeBulkResponse } from '@/api-client/generated/models';
import { useGacha } from '@/hooks/context/GachaContext';
import { useGiftCodeCrypto } from '@/hooks/context/GiftCodeCryptContext';
import { GiftCode } from '@/models/GiftCode';
import { createApiClientWithToken } from '@/service/PreRegistrationApiFactory';

type SaveSpreadsheetGiftCodeTableProps = {
  spreadsheetUrl: string;
  spreadsheetGiftCodes: GiftCode[];
  setAddedGiftCodes: React.Dispatch<React.SetStateAction<GiftCode[]>>;
};

const SaveSpreadsheetGiftCodeTable: React.FC<SaveSpreadsheetGiftCodeTableProps> = ({
  spreadsheetUrl,
  spreadsheetGiftCodes,
  setAddedGiftCodes,
}) => {
  const spreadsheetNewGitCodes = spreadsheetGiftCodes.filter(
    (spreadsheetNewGitCode) => !spreadsheetNewGitCode.isLoaded
  );
  if (!spreadsheetNewGitCodes || spreadsheetNewGitCodes.length < 1) {
    return <div />;
  }

  const { gacha } = useGacha();
  const { giftCodeCrypto } = useGiftCodeCrypto();

  const updateGiftCodeSpreadsheetData = (_spreadsheetUrl: string, giftCodeSucceededToAdd: GiftCode[]) => {
    const spreadsheetGiftCodesWithIsLoaded = spreadsheetGiftCodes.map((spreadsheetGiftCode) => {
      if (giftCodeSucceededToAdd.some((succeeded) => succeeded.code === spreadsheetGiftCode.code)) {
        return { ...spreadsheetGiftCode, isLoaded: true };
      }

      return spreadsheetGiftCode;
    });

    writeGiftCodeDataToSpreadsheet(_spreadsheetUrl, gacha.name, spreadsheetGiftCodesWithIsLoaded);
  };

  const createGiftCodeBulk = () => {
    let giftCodesBody: GiftCode[] = [];
    try {
      giftCodesBody = spreadsheetNewGitCodes.map((spreadsheetGiftCode) => ({
        ...giftCodeCrypto.encryptGiftCode(spreadsheetGiftCode),
        hash: cryptoJs.SHA3(spreadsheetGiftCode.code).toString(),
      }));
    } catch {
      notification.error({
        message: 'ギフトコードの暗号化に失敗しました。スプレッドシート上のコードを確認してください。',
      });

      return;
    }

    const api = createApiClientWithToken();
    api
      .createGiftCodeBulk(gacha.id, {
        body: {
          giftCodes: giftCodesBody,
        },
      })
      .then((response: CreateGiftCodeBulkResponse) => {
        const decryptedAddedGiftCodes = response.map((giftCode) => giftCodeCrypto.decryptGiftCode(giftCode));
        setAddedGiftCodes(decryptedAddedGiftCodes);

        updateGiftCodeSpreadsheetData(spreadsheetUrl, decryptedAddedGiftCodes);
      })
      .catch(() => {
        notification.error({
          message: 'ギフトコードの追加に失敗しました。すでに追加していないか確認してください',
        });
      });
  };

  return (
    <div>
      <div>以下のギフトコードを追加します。よろしければ下にある追加のボタンをクリックしてください。</div>
      <GiftCodeTable giftCodes={spreadsheetNewGitCodes} />
      <Button onClick={createGiftCodeBulk}>追加</Button>
    </div>
  );
};

export default SaveSpreadsheetGiftCodeTable;
